import { Col, Form as AntForm, Icon, Input, Modal, Radio, Row, Select } from 'antd';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { uid } from 'uid';
import {
  deleteCompliassistSavedSearch,
  requestSavedSearches,
  saveCompliassistSearch,
} from '@/src/compliance/redux/complianceReducer';
import { saveSearch } from '@/src/compliance/utils';
import DateRangePicker from '@/src/shared/components/DateRangePicker';
import Button from '@/src/shared/components/Button';
import Form from '@/src/shared/components/Form';
import Heading from '@/src/shared/components/Heading';
import X from '@/src/shared/components/Icon/_icons/X';
import { form as formStyle, space } from '@/styles';
import { convertMomentToDate, disableDayAfter, validateCustomRange } from '@/utils/dateTimeHelpers';
import { colMdProps, rowProps } from '@/utils/rowColHelpers';
import { multiSelectProps, singleSelectProps } from '@/utils/selectHelpers';

import style from './style';

const { Option } = Select;
const { m } = space;

const hasValue = value => {
  if (Array.isArray(value)) return value.length > 0;

  return value !== undefined && value !== null && value !== '';
};

const normalizeStates = states => {
  if (!Array.isArray(states)) return [];

  return states
    .map(state => {
      if (typeof state === 'object') return state.stateCode || state.StateCode;

      return state;
    })
    .filter(Boolean);
};

const normalizeCharts = charts => {
  if (!Array.isArray(charts)) return [];

  return charts
    .map(chart => {
      if (typeof chart === 'object') return chart.chartId || chart.ChartId;

      return chart;
    })
    .filter(Boolean);
};

const normalizeCategory = categories => {
  if (Array.isArray(categories)) {
    if (!categories.length) return undefined;

    const category = categories[0];

    if (typeof category === 'object') return category.categoryId || category.CategoryId;

    return category;
  }

  if (categories && typeof categories === 'object') {
    return categories.categoryId || categories.CategoryId;
  }

  return hasValue(categories) ? categories : undefined;
};

const normalizeDateRanges = ({ dateRanges, dateStart, dateEnd }) => {
  if (Array.isArray(dateRanges) && dateRanges[0] && dateRanges[1]) {
    return dateRanges;
  }

  if (dateStart && dateEnd) {
    return [moment(dateStart), moment(dateEnd)];
  }

  return [null, null];
};

const getFieldOptions = data => {
  return data.fieldOptions || data.FieldOptions || {};
};

const getInitialFormValues = ({ data, includeDates }) => {
  const fieldOptions = getFieldOptions(data);

  const dateStart =
    data.dateStart ||
    data.DateStart ||
    fieldOptions.dateStart ||
    fieldOptions.DateStart;

  const dateEnd =
    data.dateEnd ||
    data.DateEnd ||
    fieldOptions.dateEnd ||
    fieldOptions.DateEnd;

  const fields = {
    searchDescription: data.searchDescription || data.SearchDescription || '',
    includeOnHome: data.includeOnHome || data.IncludeOnHome || 'N',
    searchText:
      fieldOptions.searchText ||
      fieldOptions.SearchText ||
      data.searchText ||
      data.SearchText ||
      '',
    states: normalizeStates(
      fieldOptions.states ||
        fieldOptions.States ||
        data.states ||
        data.States
    ),
    categories: normalizeCategory(
      fieldOptions.categories ||
        fieldOptions.Categories ||
        data.categories ||
        data.Categories
    ),
    charts: normalizeCharts(
      fieldOptions.charts ||
        fieldOptions.Charts ||
        data.charts ||
        data.Charts
    ),
  };

  if (includeDates) {
    fields.dateRanges = normalizeDateRanges({
      dateRanges: data.dateRanges,
      dateStart,
      dateEnd,
    });
  }

  return fields;
};

const EditSavedSearch = props => {
  const {
    visible,
    closeModal,
    form,
    data = {},
    setRefreshState,
    includeDates = false,
  } = props;

  const dispatch = useDispatch();

  const personId = useSelector(state => state.auth.userinfo.PersonID);
  const searchOptions = useSelector(state => state.compliance.searchOptions);

  const {
    charts = [],
    categories = [],
    states = [],
    isLoadingOptions = false,
  } = searchOptions;

  useEffect(() => {
    if (!visible) return;

    const fields = getInitialFormValues({ data, includeDates });

    form.setFieldsValue(fields);
  }, [visible]);

  if (!visible) {
    return null;
  }

  const isRecentChanges = data.recentChanges === 'Y';

  const onSave = async () => {
    await form.validateFieldsAndScroll(async (err, values) => {
      if (err) return;

      const formattedValues = {
        ...values,
        dateStart: Array.isArray(values.dateRanges)
          ? convertMomentToDate(values.dateRanges[0])
          : null,
        dateEnd: Array.isArray(values.dateRanges)
          ? convertMomentToDate(values.dateRanges[1])
          : null,
      };

      const formattedPayload = saveSearch({
        personId,
        values: formattedValues,
        form,
        options: searchOptions,
        initialData: {
          ...data,
          personId,
        },
      });

      dispatch(
        saveCompliassistSearch({
          payload: formattedPayload,
          successCallback: closeModal,
        })
      );
    });
  };

  const handleDelete = () => {
    const searchId = data.searchId || data.SearchId || data.compChartSearchId;

    dispatch(deleteCompliassistSavedSearch(searchId));
    dispatch(requestSavedSearches({ personId }));

    if (setRefreshState) {
      setRefreshState(uid());
    }

    closeModal();
  };

  const handleCategoryChange = categoryId => {
    const chartValue = form.getFieldValue('charts');

    if (!Array.isArray(chartValue)) return;

    const hasInvalidChart = chartValue.some(chartId => {
      const chart = charts.find(ch => Number(ch.chartId) === Number(chartId));

      return !chart || !(chart.categories || [])
        .map(category => Number(category.categoryId))
        .includes(Number(categoryId));
    });

    if (hasInvalidChart) {
      form.setFieldsValue({ charts: [] });
    }
  };

  const renderMultiSelect = ({
    options,
    optCode,
    optDescription,
    label,
    _props = {},
    field,
  }) => {
    const { getFieldDecorator } = form;

    return (
      <Col {...colMdProps}>
        <AntForm.Item label={label}>
          {getFieldDecorator(field)(
            <Select {...multiSelectProps(isLoadingOptions)} {..._props}>
              {(options || []).map(opt => (
                <Option value={opt[optCode]} key={`${opt[optCode]}-${label}`}>
                  {opt[optDescription]}
                </Option>
              ))}
            </Select>
          )}
        </AntForm.Item>
      </Col>
    );
  };

  const selectedCategoryValue = form.getFieldValue('categories');

  const filteredCharts = selectedCategoryValue
    ? charts.filter(chart => {
        return (chart.categories || [])
          .map(category => Number(category.categoryId))
          .includes(Number(selectedCategoryValue));
      })
    : charts;

  return (
    <Modal
      visible={visible}
      onCancel={closeModal}
      width="80%"
      footer={
        isLoadingOptions
          ? null
          : [
              <div css={style.footerWrapper} key="delete-button">
                <span css={style.footerButtonWrapper}>
                  <Button size="small" theme="red" onClick={handleDelete}>
                    <Icon type="delete" />
                    Delete Saved Search
                  </Button>
                </span>

                <span css={style.footerButtonWrapper}>
                  <Button
                    theme="gray"
                    size="small"
                    css={style.cancel}
                    disabled={false}
                    onClick={closeModal}
                  >
                    <span css={style.cancelInner}>
                      <span css={style.cancelIcon}>
                        <X css={style.cancelIcon} />
                      </span>
                      <span css={style.cancelIcon}>Cancel</span>
                    </span>
                  </Button>

                  <Button
                    disabled={false}
                    size="small"
                    theme="default"
                    css={style.saveSearch}
                    htmlType="submit"
                    onClick={onSave}
                  >
                    <Icon type="save" theme="filled" />
                    Save Search
                  </Button>
                </span>
              </div>,
            ]
      }
      maskStyle={{
        backgroundColor: 'rgba(0,38,73,0.65)',
      }}
      title={
        isRecentChanges ? (
          <Heading type={3} weight="bolder" css={m.b0} theme="gray">
            Edit Search - Compliance Charts Recent Changes
          </Heading>
        ) : (
          <Heading type={3} weight="bolder" css={m.b0} theme="gray">
            Edit Search
          </Heading>
        )
      }
    >
      <Form>
        <Row {...rowProps}>
          <Col {...colMdProps}>
            <Heading type={3} weight="bolder" css={m.b24} theme="primaryGray">
              Search Name
            </Heading>
          </Col>
        </Row>

        <Row {...rowProps}>
          <Col {...colMdProps}>
            <AntForm.Item label="Name of Search">
              {form.getFieldDecorator('searchDescription', {
                rules: [
                  {
                    message: 'Enter Name of Search.',
                    required: true,
                  },
                ],
              })(<Input />)}
            </AntForm.Item>
          </Col>

          <Col {...colMdProps}>
            <AntForm.Item label="Show on homepage">
              {form.getFieldDecorator('includeOnHome')(
                <Radio.Group>
                  <Radio value="Y">Yes</Radio>
                  <Radio value="N">No</Radio>
                </Radio.Group>
              )}
            </AntForm.Item>
          </Col>
        </Row>

        <Row {...rowProps}>
          <Col {...colMdProps}>
            <Heading type={3} weight="bolder" css={m.b24} theme="primaryGray">
              Search Filters
            </Heading>
          </Col>
        </Row>

        <Row {...rowProps}>
          <Col {...colMdProps}>
            <AntForm.Item label="Search Text">
              {form.getFieldDecorator('searchText', {
                rules: [
                  {
                    required: false,
                  },
                ],
              })(<Input />)}
            </AntForm.Item>
          </Col>

          {renderMultiSelect({
            label: 'State',
            options: states,
            optCode: 'stateCode',
            optDescription: 'stateDescription',
            field: 'states',
          })}
        </Row>

        <Row {...rowProps}>
          <Col {...colMdProps}>
            <AntForm.Item label="Category">
              {form.getFieldDecorator('categories')(
                <Select
                  {...singleSelectProps(isLoadingOptions)}
                  onChange={categoryId => {
                    handleCategoryChange(categoryId);
                  }}
                >
                  {(categories || []).map(category => (
                    <Option value={category.categoryId} key={`${category.categoryId}-category`}>
                      {category.categoryDescription}
                    </Option>
                  ))}
                </Select>
              )}
            </AntForm.Item>
          </Col>

          {renderMultiSelect({
            label: 'Charts',
            options: filteredCharts,
            optCode: 'chartId',
            optDescription: 'chartTitle',
            field: 'charts',
          })}
        </Row>

        {includeDates && (
          <Row {...rowProps}>
            <Col {...colMdProps}>
              <AntForm.Item label="Date Range">
                {form.getFieldDecorator('dateRanges', {
                  rules: [
                    {
                      message: 'Please complete start date and end date',
                      validator: validateCustomRange,
                    },
                  ],
                })(
                  <DateRangePicker
                    size="large"
                    css={formStyle.inputGray}
                    disabledDate={disableDayAfter}
                  />
                )}
              </AntForm.Item>
            </Col>
          </Row>
        )}
      </Form>
    </Modal>
  );
};

EditSavedSearch.propTypes = {
  visible: PropTypes.bool.isRequired,
  closeModal: PropTypes.func.isRequired,
  form: PropTypes.shape({
    getFieldDecorator: PropTypes.func,
    getFieldValue: PropTypes.func,
    setFieldsValue: PropTypes.func,
    validateFieldsAndScroll: PropTypes.func,
  }).isRequired,
  data: PropTypes.shape({}).isRequired,
  setRefreshState: PropTypes.func,
  includeDates: PropTypes.bool,
};

EditSavedSearch.defaultProps = {
  setRefreshState: null,
  includeDates: false,
};

export default AntForm.create()(EditSavedSearch);